
import * as THREE from "../../../libs/three.js/build/three.module.js";
import {CURSOR_MARKER_COLOR} from "../CursorPointMarker.js";
import {DigitizationTools} from "../ToolRegistry.js";
import {computeMidpoint} from "../geometry/midpoint.js";
import {FeatureVisual} from "../FeatureVisual.js";
import {createFeature} from "../Feature.js";
import {computeScreenSpaceScale, ScreenSpaceSizes} from "../ScreenSpaceScale.js";
import {createMarker} from "./perpendicularCapture.js";

import {t, T} from "../i18n/index.js";
export {computeMidpoint};

// Same "Perpendicular / Offset geometry" family capture pattern (see
// perpendicularCapture.js's own doc comment): the two points the user places
// are throwaway helper geometry, never registered/snappable themselves - only
// the resulting midpoint is committed, as a real, independent Point feature
// (so it can be snapped to, like any other Point). Matches the architecture
// of Vinkelret punkt/linje and Parallelforskydning rather than the older
// generic capture path (which used to keep the two source vertices AND a
// non-interactive "computed point" ghost marker all alive forever as one
// combined visual).
const HELPER_COLOR = 0x00e5ff; // matches the family's baseline/helper convention

export const MidpointDigitizationTool = {
	id: "digitize_midpoint",
	name: T("Midtpunkt"),
	category: "calculated",
	kind: "registration",
	toolbarGroup: "registration",
	toolbarOrder: 80,
	icon: "midpoint.svg",
	resultGeometryType: "Point",

	tooltip: T("Beregn et 3D-midtpunkt mellem to valgte punkter. De to hjælpepunkter og hjælpelinjen fjernes igen, og midtpunktet gemmes som et selvstændigt punkt, som der kan snappes til."),

	customCapture(dm, tool){
		runMidpointCapture(dm, tool);
	},
};

DigitizationTools.register(MidpointDigitizationTool);

function runMidpointCapture(dm, tool){
	const viewer = dm.viewer;
	const domElement = viewer.renderer.domElement;

	// only a marker for the already-placed first point is needed - the
	// second point commits and finalizes in the same click, so there's
	// nothing meaningful to preview for it
	const markerA = createMarker(CURSOR_MARKER_COLOR);
	dm.scene.add(markerA);

	let pointA = null;
	let finished = false;

	const updateScales = () => {
		if(!markerA.visible) return;
		const camera = viewer.scene.getActiveCamera();
		const size = viewer.renderer.getSize(new THREE.Vector2());
		const cameraPos = camera.position;
		const s = computeScreenSpaceScale(camera, markerA.getWorldPosition(new THREE.Vector3()), cameraPos, size.width, size.height, ScreenSpaceSizes.computedPoint);
		markerA.scale.set(s, s, s);
	};
	const onUpdate = () => updateScales();
	viewer.addEventListener("update", onUpdate);

	const removeConstructionGeometry = () => {
		dm.scene.remove(markerA);
	};

	const cleanup = () => {
		domElement.removeEventListener("mouseup", onMouseUp, false);
		viewer.removeEventListener("cancel_insertions", onCancel);
		viewer.removeEventListener("update", onUpdate);
		dm.clearRubberBand();
	};

	const finalize = (a, b) => {
		const mid = computeMidpoint([a.x, a.y, a.z], [b.x, b.y, b.z]);
		const position = new THREE.Vector3(mid[0], mid[1], mid[2]);

		const visual = new FeatureVisual("Point");
		visual.appendAtEnd(position);

		const feature = createFeature({
			geometryType: "Point",
			coordinates: visual.toCoordinates(),
			category: tool.category,
			tool: tool.id,
			featureType: tool.featureType || tool.name,
			name: `${tool.name} ${dm.featureManager.features.length + 1}`,
		});

		visual.setInfo(feature.properties.name, feature.properties.description);
		dm.scene.add(visual);

		dm._wireFeatureLifecycle(feature, visual);
		dm.dispatchEvent({type: "feature_created", feature, visual});
	};

	// a not-yet-completed midpoint has nothing valid to keep - unlike
	// Line/Polygon's partial-shape concept, both helper points must exist
	// before there is a midpoint at all - so ESC and "tool switched away"
	// both simply discard the in-progress capture
	const onCancel = () => {
		if(finished) return;
		finished = true;
		cleanup();
		removeConstructionGeometry();
	};

	const onMouseUp = (e) => {
		if(finished || dm.isPaused() || dm._consumeCopyClickGuard()) return;
		if(e.button !== THREE.MOUSE.LEFT) return;

		const location = dm._pickPlacementPosition(e);
		if(!location) return;

		if(!pointA){
			pointA = location.clone();
			dm._markOperationStarted();
			markerA.position.copy(pointA);
			markerA.visible = true;
			dm.setRubberBand(pointA);
			dm._showInstruction(tool.name, t("Venstreklik for at vælge slutpunktet og beregne midtpunktet."));
			return;
		}

		const pointB = location.clone();

		finished = true;
		cleanup();
		removeConstructionGeometry();
		finalize(pointA, pointB);

		// stay active: matches the tool's previous looping behavior (unlike
		// Vinkelret/Parallelforskydning's single-shot family members) - the
		// user didn't ask for that to change, only for helper cleanup and a
		// real, snappable result point
		if(dm.activeTool === tool){
			dm.startInsertion(tool);
		}
	};

	dm._showInstruction(tool.name, t("Venstreklik for at vælge startpunktet."));
	domElement.addEventListener("mouseup", onMouseUp, false);
	viewer.addEventListener("cancel_insertions", onCancel);
}
